import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, ResourceRef, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import type { WaterListItem, WaterListParams, WaterListResponse } from '@models/waters';
import { TranslatePipe } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { toDateOnly } from '@utils/date-only';
import { loadVisibleColumns, saveVisibleColumns } from '@utils/visible-columns-storage';
import { debounceTime, distinctUntilChanged, filter, map, skip } from 'rxjs';

type FilterType = 'text' | 'dropdown' | 'boolean' | 'daterange' | 'numrange' | 'none';
type DropdownSource = 'sites' | 'countries' | 'exclusives';

interface ColumnDef {
  key: string;
  labelKey: string;
  filterType: FilterType;
  filterField?: string;
  filterFieldFrom?: string;
  filterFieldTo?: string;
  dropdownSource?: DropdownSource;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'title', labelKey: 'waters.field.title', filterType: 'none' },
  { key: 'urlTitle', labelKey: 'waters.field.urlTitle', filterType: 'none' },
  { key: 'isActive', labelKey: 'waters.field.isActive', filterType: 'boolean', filterField: 'isActive' },
  {
    key: 'country',
    labelKey: 'waters.field.country',
    filterType: 'dropdown',
    filterField: 'countryId',
    dropdownSource: 'countries',
  },
  {
    key: 'exclusive',
    labelKey: 'waters.field.exclusive',
    filterType: 'dropdown',
    filterField: 'exclusiveId',
    dropdownSource: 'exclusives',
  },
  {
    key: 'sites',
    labelKey: 'waters.field.sites',
    filterType: 'dropdown',
    filterField: 'siteId',
    dropdownSource: 'sites',
  },
  {
    key: 'spots',
    labelKey: 'waters.field.spots',
    filterType: 'numrange',
    filterFieldFrom: 'spotsMin',
    filterFieldTo: 'spotsMax',
  },
  {
    key: 'coTravelerSpots',
    labelKey: 'waters.field.coTravelerSpots',
    filterType: 'numrange',
    filterFieldFrom: 'coTravelerSpotsMin',
    filterFieldTo: 'coTravelerSpotsMax',
  },
  {
    key: 'difficulty',
    labelKey: 'waters.field.difficulty',
    filterType: 'numrange',
    filterFieldFrom: 'difficultyMin',
    filterFieldTo: 'difficultyMax',
  },
  {
    key: 'waterSize',
    labelKey: 'waters.field.waterSize',
    filterType: 'numrange',
    filterFieldFrom: 'waterSizeMin',
    filterFieldTo: 'waterSizeMax',
  },
  {
    key: 'carpWeight',
    labelKey: 'waters.field.carpWeight',
    filterType: 'numrange',
    filterFieldFrom: 'carpWeightMin',
    filterFieldTo: 'carpWeightMax',
  },
  {
    key: 'price',
    labelKey: 'waters.field.price',
    filterType: 'numrange',
    filterFieldFrom: 'priceMin',
    filterFieldTo: 'priceMax',
  },
  { key: 'pricePerSpot', labelKey: 'waters.field.pricePerSpot', filterType: 'boolean', filterField: 'pricePerSpot' },
  { key: 'dogs', labelKey: 'waters.field.dogs', filterType: 'boolean', filterField: 'dogs' },
  { key: 'rowingBoat', labelKey: 'waters.field.rowingBoat', filterType: 'boolean', filterField: 'rowingBoat' },
  { key: 'baitBoat', labelKey: 'waters.field.baitBoat', filterType: 'boolean', filterField: 'baitBoat' },
  { key: 'restaurant', labelKey: 'waters.field.restaurant', filterType: 'boolean', filterField: 'restaurant' },
  {
    key: 'hasAccommodations',
    labelKey: 'waters.field.hasAccommodations',
    filterType: 'boolean',
    filterField: 'hasAccommodations',
  },
  { key: 'ownTent', labelKey: 'waters.field.ownTent', filterType: 'boolean', filterField: 'ownTent' },
  {
    key: 'ownCaravanCamper',
    labelKey: 'waters.field.ownCaravanCamper',
    filterType: 'boolean',
    filterField: 'ownCaravanCamper',
  },
  { key: 'carOnSpot', labelKey: 'waters.field.carOnSpot', filterType: 'boolean', filterField: 'carOnSpot' },
  { key: 'week', labelKey: 'waters.field.week', filterType: 'boolean', filterField: 'week' },
  { key: 'midweek', labelKey: 'waters.field.midweek', filterType: 'boolean', filterField: 'midweek' },
  { key: 'weekend', labelKey: 'waters.field.weekend', filterType: 'boolean', filterField: 'weekend' },
  {
    key: 'averageRating',
    labelKey: 'waters.field.averageRating',
    filterType: 'numrange',
    filterFieldFrom: 'averageRatingMin',
    filterFieldTo: 'averageRatingMax',
  },
  {
    key: 'amountOfRatings',
    labelKey: 'waters.field.amountOfRatings',
    filterType: 'numrange',
    filterFieldFrom: 'amountOfRatingsMin',
    filterFieldTo: 'amountOfRatingsMax',
  },
  {
    key: 'views',
    labelKey: 'waters.field.views',
    filterType: 'numrange',
    filterFieldFrom: 'viewsMin',
    filterFieldTo: 'viewsMax',
  },
  {
    key: 'lastUpdate',
    labelKey: 'waters.field.lastUpdate',
    filterType: 'daterange',
    filterFieldFrom: 'lastUpdateFrom',
    filterFieldTo: 'lastUpdateTo',
  },
];

const DEFAULT_VISIBLE_COLUMNS = [
  'title',
  'isActive',
  'country',
  'exclusive',
  'sites',
  'spots',
  'price',
  'averageRating',
  'views',
  'lastUpdate',
];

const FILTERS_SESSION_KEY = 'water-list-filters';
const COLUMNS_STORAGE_KEY = 'water-list-columns';

type DropdownValue = number | string | boolean | null;
type RangeValue = string | number | null;

interface PersistedFilters {
  search: string;
  textFilters: Record<string, string>;
  dropdownFilters: Record<string, DropdownValue>;
  rangeFilters: Record<string, RangeValue>;
}

@Component({
  selector: 'app-water-list',
  templateUrl: './water-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    CurrencyPipe,
    DecimalPipe,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslatePipe,
    BoolComponent,
  ],
})
export class WaterListComponent {
  private readonly watersService = inject(CrmWatersService);
  readonly codeListsService = inject(CodeListsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  readonly dropdownSources: Record<DropdownSource, () => { id: number | string; name: string }[]> = {
    sites: this.codeListsService.sites,
    countries: this.codeListsService.countries,
    exclusives: this.codeListsService.exclusives,
  };

  readonly allColumns = ALL_COLUMNS;

  readonly visibleColumnKeys = signal<string[]>(
    loadVisibleColumns(
      COLUMNS_STORAGE_KEY,
      this.route.snapshot.queryParamMap.get('columns'),
      ALL_COLUMNS.map((c) => c.key),
      DEFAULT_VISIBLE_COLUMNS,
    ),
  );
  readonly displayedColumns = computed(() => [...this.visibleColumnKeys(), 'actions']);
  readonly filterColumns = computed(() => this.displayedColumns().map((k) => `filter-${k}`));

  readonly page = signal(1);
  readonly pageSize = signal(25);

  private readonly persisted = WaterListComponent.loadPersisted();

  readonly searchInput = signal(this.persisted.search);
  private readonly searchQuery = signal(this.persisted.search);

  private readonly textFilterInputs = signal<Record<string, string>>(this.persisted.textFilters);
  private readonly textFilters = signal<Record<string, string>>(this.persisted.textFilters);

  readonly dropdownFilters = signal<Record<string, DropdownValue>>(this.persisted.dropdownFilters);
  readonly rangeFilters = signal<Record<string, RangeValue>>(this.persisted.rangeFilters);

  private readonly requestParams = computed<WaterListParams>(() => {
    const tf = this.textFilters();
    const df = this.dropdownFilters();
    const rf = this.rangeFilters();
    const entries: [string, unknown][] = [];
    for (const col of ALL_COLUMNS) {
      if (col.filterType === 'text' && col.filterField) {
        entries.push([col.filterField, tf[col.filterField] || null]);
      } else if ((col.filterType === 'dropdown' || col.filterType === 'boolean') && col.filterField) {
        entries.push([col.filterField, df[col.filterField] ?? null]);
      } else if (
        (col.filterType === 'daterange' || col.filterType === 'numrange') &&
        col.filterFieldFrom &&
        col.filterFieldTo
      ) {
        entries.push([col.filterFieldFrom, rf[col.filterFieldFrom] ?? null]);
        entries.push([col.filterFieldTo, rf[col.filterFieldTo] ?? null]);
      }
    }
    return {
      search: this.searchQuery() || null,
      page: this.page(),
      pageSize: this.pageSize(),
      ...Object.fromEntries(entries),
    } as WaterListParams;
  });

  readonly watersResource: ResourceRef<WaterListResponse | undefined> = rxResource({
    params: () => this.requestParams(),
    stream: ({ params }) =>
      this.watersService.getWaters(params).pipe(showGenericErrorToast(this.toastService, () => undefined)),
  });

  readonly waters = toSignal(
    toObservable(this.watersResource.value).pipe(
      filter((v): v is WaterListResponse => v !== undefined),
      map((v) => v.items),
    ),
    { initialValue: [] as WaterListItem[] },
  );
  readonly totalCount = toSignal(
    toObservable(this.watersResource.value).pipe(
      filter((v): v is WaterListResponse => v !== undefined),
      map((v) => v.totalCount),
    ),
    { initialValue: 0 },
  );

  constructor() {
    this.syncQueryParams();

    toObservable(this.searchInput)
      .pipe(skip(1), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((v) => {
        this.searchQuery.set(v);
        this.page.set(1);
        this.persistFilters();
      });

    toObservable(this.textFilterInputs)
      .pipe(skip(1), debounceTime(300), takeUntilDestroyed())
      .subscribe((v) => {
        this.textFilters.set(v);
        this.page.set(1);
        this.persistFilters();
      });
  }

  onSearchInput(value: string): void {
    this.searchInput.set(value);
  }

  onTextFilterInput(filterField: string, value: string): void {
    this.textFilterInputs.update((prev) => ({ ...prev, [filterField]: value }));
  }

  onDropdownFilterChange(filterField: string, value: DropdownValue | ''): void {
    this.dropdownFilters.update((prev) => ({ ...prev, [filterField]: value === '' ? null : value }));
    this.page.set(1);
    this.persistFilters();
  }

  onRangeFilterChange(field: string, value: string, isNumber: boolean): void {
    let parsed: RangeValue;
    if (value === '') {
      parsed = null;
    } else if (isNumber) {
      const num = Number(value);
      parsed = Number.isFinite(num) ? num : null;
    } else {
      parsed = value;
    }
    this.rangeFilters.update((prev) => ({ ...prev, [field]: parsed }));
    this.page.set(1);
    this.persistFilters();
  }

  onDateFilterChange(field: string, date: Date | null): void {
    const iso = toDateOnly(date);
    this.rangeFilters.update((prev) => ({ ...prev, [field]: iso }));
    this.page.set(1);
    this.persistFilters();
  }

  getDateFilterValue(field: string): Date | null {
    const v = this.rangeFilters()[field];
    if (typeof v !== 'string' || !v) return null;
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  clearAllFilters(): void {
    this.searchInput.set('');
    this.searchQuery.set('');
    this.textFilterInputs.set({});
    this.textFilters.set({});
    this.dropdownFilters.set({});
    this.rangeFilters.set({});
    this.page.set(1);
    this.persistFilters();
  }

  readonly hasActiveFilters = computed(
    () =>
      !!this.searchInput() ||
      Object.values(this.textFilterInputs()).some((v) => !!v) ||
      Object.values(this.dropdownFilters()).some((v) => v != null) ||
      Object.values(this.rangeFilters()).some((v) => v != null),
  );

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex + 1);
    this.pageSize.set(event.pageSize);
  }

  toggleColumn(key: string): void {
    const current = this.visibleColumnKeys();
    const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    if (updated.length === 0) return;
    const ordered = ALL_COLUMNS.map((c) => c.key).filter((k) => updated.includes(k));
    this.visibleColumnKeys.set(ordered);
    saveVisibleColumns(COLUMNS_STORAGE_KEY, ordered);
    this.syncQueryParams();
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      queryParams: { columns: this.visibleColumnKeys().join(',') },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  isColumnVisible(key: string): boolean {
    return this.visibleColumnKeys().includes(key);
  }

  getDropdownFilterValue(filterField: string): DropdownValue | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : v;
  }

  getBooleanFilterValue(filterField: string): boolean | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : (v as boolean);
  }

  getTextFilterValue(filterField: string): string {
    return this.textFilterInputs()[filterField] ?? '';
  }

  getRangeFilterValue(field: string): string {
    const v = this.rangeFilters()[field];
    return v == null ? '' : String(v);
  }

  getSiteNames(siteIds: number[]): string {
    return siteIds.map((id) => this.codeListsService.siteMap().get(id) ?? id).join(', ');
  }

  private persistFilters(): void {
    const payload: PersistedFilters = {
      search: this.searchInput(),
      textFilters: this.textFilterInputs(),
      dropdownFilters: this.dropdownFilters(),
      rangeFilters: this.rangeFilters(),
    };
    try {
      sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("sessionStorage unavailable — filters simply won't persist.", e);
    }
  }

  private static loadPersisted(): PersistedFilters {
    const empty: PersistedFilters = { search: '', textFilters: {}, dropdownFilters: {}, rangeFilters: {} };
    try {
      const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
      if (!raw) return empty;
      return { ...empty, ...(JSON.parse(raw) as Partial<PersistedFilters>) };
    } catch {
      return empty;
    }
  }

  navigateToCreate(): void {
    this.router.navigate(['/waters/create'], { queryParams: { backUrl: this.router.url } });
  }

  navigateToWater(id: number): void {
    this.router.navigate(['/waters/edit', id], { queryParams: { backUrl: this.router.url } });
  }
}
